import { Database, FileText, LayoutTemplate, RefreshCw, Table2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { useRuntimeConfig } from "@/hooks/use-runtime-config";
import { apiClient } from "@/services/api";

function EmbeddedView({ title, src }: { title: string; src: string }) {
  return (
    <div className="flex h-[600px] flex-col rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <span className="text-sm font-medium">{title}</span>
        <a href={src} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
          新窗口打开
        </a>
      </div>
      <iframe src={src} className="flex-1 rounded-b-lg" title={title} allow="fullscreen" />
    </div>
  );
}

function BaserowPanel({ url }: { url: string }) {
  const [apps, setApps] = React.useState<Array<{ id: number; name: string }>>([]);
  const [tables, setTables] = React.useState<Array<{ id: number; name: string }>>([]);
  const [rows, setRows] = React.useState<Record<string, unknown>[]>([]);
  const [selectedApp, setSelectedApp] = React.useState<string>("");
  const [selectedTable, setSelectedTable] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadApps = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/baserow/applications");
      const list = (response.data as Array<{ id: number; name: string; type: string }>).filter(
        (a) => a.type === "database",
      );
      setApps(list);
      if (list.length > 0 && !selectedApp) {
        setSelectedApp(String(list[0].id));
      }
    } catch (err) {
      setError(`加载应用失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTables = async (appId: string) => {
    if (!appId) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/baserow/databases/${appId}/tables`);
      const list = response.data as Array<{ id: number; name: string }>;
      setTables(list);
      if (list.length > 0) {
        setSelectedTable(String(list[0].id));
      } else {
        setSelectedTable("");
        setRows([]);
      }
    } catch (err) {
      setError(`加载表格失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRows = async (tableId: string) => {
    if (!tableId) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/baserow/tables/${tableId}/rows`);
      const data = response.data as { results?: Record<string, unknown>[] };
      setRows((data.results || []) as Record<string, unknown>[]);
    } catch (err) {
      setError(`加载数据失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void loadApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void loadTables(selectedApp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp]);

  React.useEffect(() => {
    void loadRows(selectedTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable]);

  const columns = rows.length > 0 ? Object.keys(rows[0]).filter((k) => k !== "id" && !k.startsWith("_")) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">数据库</label>
          <Select value={selectedApp} onValueChange={setSelectedApp}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="选择数据库" />
            </SelectTrigger>
            <SelectContent>
              {apps.map((app) => (
                <SelectItem key={app.id} value={String(app.id)}>
                  {app.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">表格</label>
          <Select value={selectedTable} onValueChange={setSelectedTable}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="选择表格" />
            </SelectTrigger>
            <SelectContent>
              {tables.map((table) => (
                <SelectItem key={table.id} value={String(table.id)}>
                  {table.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" disabled={loading} onClick={() => void loadRows(selectedTable)}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          刷新
        </Button>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">暂无数据</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col}>{col}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell key={col}>{String(row[col] ?? "")}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <EmbeddedView title="Baserow 完整视图" src={url} />
    </div>
  );
}

function StrapiPanel({ url }: { url: string }) {
  const [contentTypes, setContentTypes] = React.useState<Array<{ uid: string; schema?: { name?: string } }>>([]);
  const [entries, setEntries] = React.useState<Record<string, unknown>[]>([]);
  const [selectedType, setSelectedType] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/strapi/content-types");
      const list = response.data as Array<{ uid: string; schema?: { name?: string } }>;
      const apiTypes = list.filter((t) => t.uid?.startsWith("api::"));
      setContentTypes(apiTypes);
      if (apiTypes.length > 0 && !selectedType) {
        setSelectedType(apiTypes[0].uid);
      }
    } catch (err) {
      setError(`加载内容类型失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async (type: string) => {
    if (!type) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/strapi/${type}`);
      const data = response.data as { data?: Record<string, unknown>[] };
      setEntries((data.data || []) as Record<string, unknown>[]);
    } catch (err) {
      setError(`加载内容失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void loadEntries(selectedType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const attributes =
    entries.length > 0 && entries[0].attributes
      ? Object.keys(entries[0].attributes as Record<string, unknown>)
      : ["id", "title", "createdAt"];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">内容类型</label>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="选择内容类型" />
            </SelectTrigger>
            <SelectContent>
              {contentTypes.map((type) => (
                <SelectItem key={type.uid} value={type.uid}>
                  {type.schema?.name || type.uid}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" disabled={loading} onClick={() => void loadEntries(selectedType)}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          刷新
        </Button>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">暂无内容</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {attributes.map((attr) => (
                  <TableHead key={attr}>{attr}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry, idx) => {
                const attrs = (entry.attributes as Record<string, unknown>) || {};
                return (
                  <TableRow key={idx}>
                    {attributes.map((attr) => (
                      <TableCell key={attr}>
                        {typeof attrs[attr] === "string"
                          ? String(attrs[attr]).slice(0, 80)
                          : String(attrs[attr] ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <EmbeddedView title="Strapi Admin" src={`${url}/admin`} />
    </div>
  );
}

function RAGPanel() {
  const [file, setFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [question, setQuestion] = React.useState("");
  const [answer, setAnswer] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/knowledge/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      alert("上传成功");
    } catch (error) {
      alert(`上传失败：${String(error)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleQuery = async () => {
    if (!question.trim()) return;
    setLoading(true);
    try {
      const response = await apiClient.post("/knowledge/query", { question });
      const chunks = response.data.chunks as { content: string; similarity: number }[];
      setAnswer(
        chunks.length > 0
          ? chunks.map((c, i) => `${i + 1}. ${c.content} (相似度: ${c.similarity.toFixed(3)})`).join("\n\n")
          : "未找到相关知识。",
      );
    } catch (error) {
      setAnswer(`查询失败：${String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            上传文档
          </CardTitle>
          <CardDescription className="text-xs">支持 .txt / .md / .json 等文本文件，自动分块并向量化。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <Button onClick={() => void handleUpload()} disabled={!file || uploading} className="w-full">
            {uploading ? "上传中..." : "上传并向量化"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" />
            知识库问答
          </CardTitle>
          <CardDescription className="text-xs">基于 pgvector 语义检索。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="输入问题，例如：客户管理系统的数据模型包含哪些字段？"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
          />
          <Button onClick={() => void handleQuery()} disabled={loading || !question.trim()} className="w-full">
            {loading ? "检索中..." : "检索"}
          </Button>
          {answer && <Textarea value={answer} readOnly rows={6} className="text-xs" />}
        </CardContent>
      </Card>
    </div>
  );
}

export function DataViewPage() {
  const { config, loading } = useRuntimeConfig();

  // 三个可选工具走运行时配置下发：未配置的入口直接隐藏，指不回访问者本机。
  const defaultTab = config.supabaseStudioUrl
    ? "supabase"
    : config.baserowUrl
      ? "baserow"
      : config.strapiUrl
        ? "strapi"
        : "rag";
  const hasOptionalTool = Boolean(config.supabaseStudioUrl || config.baserowUrl || config.strapiUrl);

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">数据管理</h1>
        <p className="text-sm text-muted-foreground">集成 Supabase、Baserow、Strapi 与 RAG 知识库。</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">正在读取运行时配置…</p>
      ) : (
        <>
          {!hasOptionalTool && (
            <p className="text-sm text-muted-foreground">
              未配置 Supabase Studio / Baserow / Strapi 入口，相关页面已隐藏。需要时在部署侧设置
              FRONTEND_SUPABASE_STUDIO_URL、FRONTEND_BASEROW_URL、FRONTEND_STRAPI_URL 后重新加载。
            </p>
          )}

          <Tabs defaultValue={defaultTab}>
            <TabsList>
              {config.supabaseStudioUrl && (
                <TabsTrigger value="supabase">
                  <Database className="mr-1 h-4 w-4" />
                  Supabase
                </TabsTrigger>
              )}
              {config.baserowUrl && (
                <TabsTrigger value="baserow">
                  <Table2 className="mr-1 h-4 w-4" />
                  Baserow
                </TabsTrigger>
              )}
              {config.strapiUrl && (
                <TabsTrigger value="strapi">
                  <LayoutTemplate className="mr-1 h-4 w-4" />
                  Strapi
                </TabsTrigger>
              )}
              <TabsTrigger value="rag">
                <FileText className="mr-1 h-4 w-4" />
                知识库
              </TabsTrigger>
            </TabsList>

            {config.supabaseStudioUrl && (
              <TabsContent value="supabase" className="space-y-4 data-[state=inactive]:hidden">
                <EmbeddedView title="Supabase Studio" src={config.supabaseStudioUrl} />
              </TabsContent>
            )}

            {config.baserowUrl && (
              <TabsContent value="baserow" className="space-y-4 data-[state=inactive]:hidden">
                <BaserowPanel url={config.baserowUrl} />
              </TabsContent>
            )}

            {config.strapiUrl && (
              <TabsContent value="strapi" className="space-y-4 data-[state=inactive]:hidden">
                <StrapiPanel url={config.strapiUrl} />
              </TabsContent>
            )}

            <TabsContent value="rag" className="data-[state=inactive]:hidden">
              <RAGPanel />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
